/* eslint-disable no-restricted-syntax */
/* eslint-disable indent */
/** @jsxImportSource @emotion/react */
import { faLink } from "@fortawesome/pro-regular-svg-icons";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { LAYER_TYPE } from "../../../../actions/Layers/constants";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { LayerTypeConfig } from "../../../../actions/Tenants/config/constantsTyped";
import {
    useAddLayerChildMutation,
    useAddLayerParentMutation,
    useCreateAndAddLayerChildMutation,
    usePatchLayerMutation,
    useRemoveLayerChildMutation
} from "../../../../hooks/mutations";
import { useLayerChecksInvalidation } from "../../../../hooks/useLayerChecks";
import { findChanges } from "../../../../utils/findChanges";
import { toast } from "../../../../utils/toast";
import LayerAutoComplete from "../../../Forms/LayerAutoComplete";
// import QRCodeScanner from "../../../Forms/QRCodeScanner";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../utils";
import QRCodeScan from "../../../Forms/QRCodeScan";
import { searchLayers } from "../../../Forms/utils";
import { Button } from "../../../Helper/Button";


import { useRouteLayer } from "../../../../hooks/useCurrentEntities";

interface LayerItem {
    id?: string;
    type?: string;
    label?: string;
    fruit_type?: string;
}

interface CreateLayerRelationModalState {
    isOpen: boolean;
    linking_layer: any;
    set_child: boolean;
    layer_type: LAYER_TYPE;
    parent?: any;
    children?: LayerItem[];
}

export default function CreateLayerRelationModal() {
    const config = useConfig();
    const layer = useRouteLayer();
    const layer_type = config.get_layer_config(layer) as LayerTypeConfig;
    const parent_layer_type = layer_type.parent_layer_type ? config.get_raw_layer_config(layer_type.parent_layer_type as string) : undefined;
    const children_layer_type = layer_type.children_layer_type ? config.get_raw_layer_config(layer_type.children_layer_type as string) : undefined;
    const show_link_layer_button = (parent_layer_type || children_layer_type) && layer_type.show_link_layer_button;
    const layer_children = useRouteLayer().children ?? [];
    const filteredLayerChildren = children_layer_type ? layer_children.filter((c) => c.type === children_layer_type.value) : layer_children;
    const params = useParams();
    const tenantId = params.tenant_id as string;
    const navigate = useNavigate();

    // Mutation hooks
    const addChildMutation = useAddLayerChildMutation();
    const addParentMutation = useAddLayerParentMutation();
    const createAddChildMutation = useCreateAndAddLayerChildMutation();
    const removeChildMutation = useRemoveLayerChildMutation();
    const patchLayerMutation = usePatchLayerMutation();
    const [scannerOpen, setScannerOpen] = useState(true);
    const [state, setState] = useState<CreateLayerRelationModalState>({
        isOpen: false,
        linking_layer: false,
        set_child: false,
        layer_type: LAYER_TYPE.PALLET,
        parent: undefined,
        children: filteredLayerChildren
    });
    const [error, setError] = useState<string | null>(null);
    const [isAttributeNoteModalOpen, setIsAttributeNoteModalOpen] = useState(false);

    // * Use hook to invalidate layer checks after linking layers
    const { invalidateLayerChecks } = useLayerChecksInvalidation(tenantId, layer?.id);

    const matchingAttributeName = layer_type?.matching_attribute_name;
    const expectedAttributeValues = useMemo(() => (layer_type?.enable_matching_attribute && matchingAttributeName && layer?.[matchingAttributeName] ? [layer?.[matchingAttributeName]] : []), [layer, matchingAttributeName]);

    const unexpectedAttributeChildren = useMemo(() => (matchingAttributeName ? state?.children?.filter((c) => {
        const attributeValue = (c as any).meta ? (c as any)?.meta?.find((m) => m.key === matchingAttributeName)?.value : c[matchingAttributeName];
        const hasNote = (c as any).meta ? (c as any)?.meta?.find((m) => m.key === "unexpected_attribute_note")?.value : (c as any).unexpected_attribute_note;
        return !hasNote && (matchingAttributeName && expectedAttributeValues.length && attributeValue && !expectedAttributeValues.includes(attributeValue));
    }) : []), [state?.children, matchingAttributeName, expectedAttributeValues]);

    const [notes, setNotes] = useState<{ [key: string]: string }>({});
    const warning = useMemo(() => {
        if (layer_type?.enable_matching_attribute && matchingAttributeName && unexpectedAttributeChildren?.length) {
            return `Wrong ${matchingAttributeName}: ${unexpectedAttributeChildren.map((i: any) => (i.meta ? i?.meta?.find((m) => m.key === matchingAttributeName)?.value : i[matchingAttributeName])).filter(Boolean).join(", ")}`;
        }

        return null;
    }, [unexpectedAttributeChildren]);

    const handleNoteChange = (id: string, value: string) => {
        setNotes({ ...notes, [id]: value });
    };

    useEffect(() => {
        if (params.action === "link-parent") {
            openParentModal();
        }
        if (params.action === "link-children") {
            openChildrenModal();
        }
    }, [params.action]);

    const openParentModal = () => {
        setState({
            isOpen: true,
            linking_layer: parent_layer_type,
            set_child: false,
            layer_type: parent_layer_type!.value,
            parent: undefined
        });
    };

    const openChildrenModal = () => {
        setState({
            isOpen: true,
            linking_layer: children_layer_type,
            set_child: true,
            layer_type: children_layer_type!.value,
            children: filteredLayerChildren
        });
    };

    const refreshIndex = () => {
        // * Invalidate layer checks cache to refresh the list
        invalidateLayerChecks();
    };

    const _startLinking = async () => {
        try {
            if (state.set_child && warning && Object.keys(notes).length) {
                await Promise.all((unexpectedAttributeChildren ?? []).map((child) => {
                    if (!child.id || !notes[child.id]) {
                        return Promise.resolve();
                    }
                    return patchLayerMutation.mutateAsync({
                        layer: child,
                        field: "unexpected_attribute_note",
                        value: notes[child.id],
                        keep_history: true,
                    });
                }));
                setIsAttributeNoteModalOpen(false);
                setNotes({});
            }

            if (state.set_child && Array.isArray(state.children)) {
                const { toBeAdded, toBeRemoved } = findChanges(filteredLayerChildren, state.children, (x, y) => x.label === y.label);
                await Promise.all([
                    ...toBeAdded.flatMap(({ id, label, type }) => [
                        id ? addChildMutation.mutateAsync({ layer, childLayerId: id }) : Promise.resolve(),
                        label ? createAddChildMutation.mutateAsync({ layer, label, type: type || state.layer_type }) : Promise.resolve(),
                    ]),
                    ...toBeRemoved.map((child) => removeChildMutation.mutateAsync({ layer, childLayerId: child.id! })),
                ]);
            } else if (!state.set_child && state.parent?.id) {
                await addParentMutation.mutateAsync({ layer, parentLayerId: state.parent.id });
            } else {
                toast.error(state.set_child ? "No children selected" : "No parent selected");
                return;
            }

            refreshIndex();
            setState({ ...state, isOpen: false });
            if (layer_type?.show_link_layer_button && layer_type?.show_link_layer_button !== true) {
                navigate(`/layer/${layer.id}/action/${layer_type.show_link_layer_button}`, { replace: true });
            }
        } catch (error) {
            toast.error("Could not update the layer relationship. Please try again.");
        }
    };

    const checkChildrenLimits = (items) => {
        if (!layer_type?.enable_children_limits || (!layer_type?.max_children && !layer_type?.max_children_attribute_name)) {
            return;
        }

        const maxPallets = layer_type?.max_children_attribute_name ? Number(layer?.[layer_type?.max_children_attribute_name]) : layer_type?.max_children;

        if (maxPallets && (items?.length > maxPallets)) {
            setError(`Cannot link more than ${maxPallets} pallets in the order. Please remove some linked pallets.`);
        }

        if (error && maxPallets && (items?.length <= maxPallets)) {
            setError(null);
        }
    };

    const setParent = (parent) => setState((state) => ({ ...state, parent }));

    const setChildren = (children) => {
        const items = (children ?? []).filter((i) => i.type === children_layer_type!.value);

        // Prevent duplicates in multi-select by label/id.
        // This is especially important for barcode scans which may emit the same value multiple times.
        const seen = new Set<string>();
        const uniqueItems: any[] = [];
        items.forEach((i) => {
            const key = i?.label ?? i?.id;
            if (!key) {
                uniqueItems.push(i);
                return;
            }
            const keyStr = String(key);
            if (seen.has(keyStr)) return;
            seen.add(keyStr);
            uniqueItems.push(i);
        });

        checkChildrenLimits(uniqueItems);

        setState((state) => ({ ...state, children: uniqueItems }));
    };

    const canCreateChildren = children_layer_type?.is_creatable !== false;
    const canCreateParent = parent_layer_type?.is_creatable !== false;
    const disableMultipleParents = children_layer_type?.disable_multiple_parents;

    const handleScan = (result: string) => {
        // * result is already filtered
        // const filteredResult = config.barcode_filter(result);
        const filteredResult = result;
        /**
         * TODO: @caution we only handle scan for parent also
         * This handle scan only for children
         */
        const emptyResult = (filteredResult: string) => {
            if (filteredResult && state.set_child) {
                // * If the result is not found in the children list, we add it
                if (!getFilledArrayOrDefault(state.children).find((i) => i.label === filteredResult) && canCreateChildren) {
                    setChildren([...(state.children ?? []), { label: filteredResult }]);
                }
            }
        };

        const searchResult = (data: LayerItem[]) => {
            if (data.length > 0 && state.set_child) {
                // !warning: Add all search results here
                setChildren([...(state.children ?? []), ...data]);
            } else {
                // if it doesn't exist we create a new layer, if allowed
                emptyResult(filteredResult);
            }
        };

        const handleError = (error: Error) => {
            toast.error(error.message);
        };

        searchLayers(filteredResult, state.layer_type, state.set_child && disableMultipleParents ? "only-empty" : undefined)
            .then(searchResult)
            .catch(handleError);
    };

    return (
        <>
            {
                show_link_layer_button && <>
                    {parent_layer_type && (
                        <Button
                            color="secondary"
                            outline
                            onClick={openParentModal}
                            icon={faLink}
                        >
                            Link {parent_layer_type.text}
                        </Button>
                    )}

                    {children_layer_type && (
                        <Button
                            color="secondary"
                            outline
                            onClick={openChildrenModal}
                            icon={faLink}
                        >
                            Link {children_layer_type.text}
                        </Button>
                    )}
                </>
            }

            <Modal key={1} isOpen={state.isOpen} size="md">
                <ModalHeader>Link {state.linking_layer?.text}</ModalHeader>

                <ModalBody>
                    <div>
                        <Row className="align-items-center justify-content-center pb-2">
                            <Col>
                                <Label>{state.linking_layer?.text}</Label>

                                {state.set_child && scannerOpen && <div className="pb-2">
                                    <QRCodeScan
                                        onScan={handleScan}
                                        barcode_filter={config.barcode_filter}
                                    />
                                </div>}

                                <LayerAutoComplete
                                    type={state.layer_type}
                                    creatable={state.set_child ? canCreateChildren : canCreateParent}
                                    multi={state.set_child}
                                    layer={state.set_child ? state.children : state.parent}
                                    setLayer={state.set_child ? setChildren : setParent}
                                    onlyOrphanLayers={state.set_child && !!children_layer_type?.disable_multiple_parents}
                                />
                            </Col>
                        </Row>

                        {isFilledArray(expectedAttributeValues) && <div>Expected variety codes: {expectedAttributeValues.join(", ")}</div>}
                        {warning && <Alert color="warning">{warning}</Alert>}
                        {error && <Alert color="danger">{error}</Alert>}
                    </div>
                </ModalBody>

                <ModalFooter>
                    {state.set_child && <Button color="light" className="me-auto" onClick={() => setScannerOpen(!scannerOpen)}>{scannerOpen ? "Stop Scanner" : "Start Scanner"}</Button> }
                    <Button color="light" onClick={() => setState({ ...state, isOpen: false })}>Cancel</Button>
                    <Button color={warning || error ? "danger" : "success"} onClick={warning ? () => setIsAttributeNoteModalOpen(true) : _startLinking} icon={faLink} disabled={!!error}>Link</Button>
                </ModalFooter>
            </Modal>

            <Modal key={2} isOpen={isAttributeNoteModalOpen} size="md">
                <ModalHeader>Update {children_layer_type?.text} note</ModalHeader>
                <ModalBody>
                    <div className="d-flex flex-column gap-2">
                        {unexpectedAttributeChildren?.map((c, index) => (
                            <div key={index} className="d-flex flex-column gap-1">
                                <Label>{children_layer_type?.text}: {c.label}</Label>
                                <Input value={notes[c.id!] || (c as any).unexpected_attribute_note} onChange={(e) => handleNoteChange(c.id!, e.target.value)} />
                            </div>
                        ))}
                    </div>
                </ModalBody>

                <ModalFooter>
                    <Button color="light" onClick={() => setIsAttributeNoteModalOpen(false)}>Cancel</Button>
                    <Button
                        color="success"
                        onClick={_startLinking}
                        icon={faLink}
                        disabled={unexpectedAttributeChildren?.length !== Object.keys(notes).length}
                    >
                        Save and link
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
}
